import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Compass, Heart, LogIn, UserCircle2, ShoppingBag } from 'lucide-react';
import { HOME, TRAILS, WISHLIST } from '@/constants/testIds';
import BrandLogo from '@/components/BrandLogo';
import WishlistPanel from '@/components/WishlistPanel';
import useWishlist from '@/hooks/useWishlist';
import useUser from '@/hooks/useUser';
import { HERITAGE_BUNDLES } from '@/data/catalog';

// Ribbon dismiss flag lives in localStorage so a closed ribbon stays
// closed across sessions but a re-enabled promotion (new max saving)
// re-opens the ribbon by carrying the current key.
const bundleTopSaving = () => {
  let max = 0;
  let label = '£20.90';
  for (const b of HERITAGE_BUNDLES) {
    const m = (b.saving || '').match(/£([\d.]+)/);
    if (m) {
      const v = parseFloat(m[1]);
      if (v > max) {
        max = v;
        label = `£${m[1]}`;
      }
    }
  }
  return label;
};

// Ribbon copy rotates on a fortnightly (14-day) cadence so the strip
// stays fresh rather than being ignored. The rotation is deterministic
// (based on ISO week / 2) so every visitor sees the same message in the
// same fortnight — no A/B split, just quiet variety.
const ribbonMessages = (savingLabel) => [
  {
    eyebrow: 'Bundles',
    body: (
      <>Save up to <span className="font-bold">{savingLabel}</span> on our curated Heritage Estate kits.</>
    ),
    cta: 'Shop the range',
  },
  {
    eyebrow: 'New',
    body: <>The Deep Winter Kit — a Hex Block Fire Steel + Hemp Tinder for January weather.</>,
    cta: 'See the kit',
  },
  {
    eyebrow: 'Sub-£50',
    body: <>The Starter — an EDC fire steel necklace + toggle for <span className="font-bold">£44.95</span>.</>,
    cta: 'View The Starter',
  },
  {
    eyebrow: 'Highland Estate',
    body: <>Built for the hill, not the Instagram — eleven honest tools in our collection.</>,
    cta: 'Browse the range',
  },
];

const currentRibbonIndex = (list) => {
  // 14-day fortnight buckets from a fixed epoch so rotation is stable
  // across a load-balanced deploy. Epoch: Mon 2026-01-05, UTC.
  const epoch = Date.UTC(2026, 0, 5);
  const fortnight = 14 * 24 * 60 * 60 * 1000;
  const idx = Math.floor((Date.now() - epoch) / fortnight);
  return ((idx % list.length) + list.length) % list.length;
};

// Scroll-anchor links (targets are section IDs on the home page).
// `primary: true` shows on desktop; everything else is only in the
// mobile menu so the enlarged brand mark has room to breathe.
const SCROLL_LINKS = [
  { id: 'about', label: 'Our Story', testId: HOME.navAbout, primary: true },
  { id: 'ebikes', label: 'E-Bikes', testId: HOME.navEbikes, primary: true },
  { id: 'camping', label: 'Camping', testId: HOME.navCamping, primary: true },
  { id: 'adaptive', label: 'Adaptive', testId: HOME.navAdaptive, primary: false },
];
// Route links (proper pages).
const ROUTE_LINKS = [
  { path: '/heritage-estate', label: 'Heritage Estate', testId: 'nav-heritage-estate', primary: true },
  { path: '/trails', label: 'Trails', testId: TRAILS.navTrails, primary: false },
  { path: '/sourcing', label: 'Sourcing', testId: 'nav-sourcing', primary: false },
  { path: '/blog', label: 'Blog', testId: 'nav-blog', primary: false },
  { path: '/field-notes', label: 'Field Notes', testId: 'nav-field-notes', primary: false },
];
const CONTACT_LINK = { path: '/contact', label: 'Contact', testId: HOME.navContact };

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const onHome = location.pathname === '/';
  const { count: wishlistCount } = useWishlist();
  const { user, loading: userLoading } = useUser();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    if (!onHome) {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goToRoute = (path) => {
    setOpen(false);
    navigate(path);
  };

  // Nav is always solid now that the hero uses a compact image banner
  // over the parchment ground (rather than a full-viewport dark photo).
  // Kept as a `const` (not a state) for clarity and future flexibility.
  const solid = true;
  const [ribbonDismissed, setRibbonDismissed] = useState(() => {
    try { return localStorage.getItem('bundle-ribbon-dismissed') === '1'; }
    catch { return false; }
  });
  const dismissRibbon = () => {
    setRibbonDismissed(true);
    try { localStorage.setItem('bundle-ribbon-dismissed', '1'); } catch { /* ignore */ }
  };
  const savingLabel = bundleTopSaving();
  const rMessages = ribbonMessages(savingLabel);
  const rIdx = currentRibbonIndex(rMessages);
  const activeRibbon = rMessages[rIdx];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        solid
          ? 'bg-[var(--brand-parchment)] border-b border-[var(--brand-stone)]'
          : 'bg-transparent'
      }`}
    >
      {!ribbonDismissed && (
        <div
          data-testid="nav-bundle-ribbon"
          data-ribbon-index={rIdx}
          className="relative bg-[var(--brand-forest-deep)] text-[var(--brand-parchment)] text-xs sm:text-sm"
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2 flex items-center justify-center gap-3 sm:gap-4 flex-wrap">
            <span className="inline-flex items-center gap-2">
              <ShoppingBag size={14} className="text-[var(--brand-gold)]" strokeWidth={2.2} />
              <span className="uppercase tracking-[0.16em] font-semibold text-[var(--brand-gold)]">
                {activeRibbon.eyebrow}
              </span>
              <span className="opacity-90">{activeRibbon.body}</span>
            </span>
            <Link
              to="/heritage-estate"
              data-testid="nav-bundle-ribbon-link"
              className="underline underline-offset-2 font-semibold hover:text-[var(--brand-gold)]"
            >
              {activeRibbon.cta} &rarr;
            </Link>
          </div>
          <button
            onClick={dismissRibbon}
            aria-label="Dismiss bundles promotion"
            data-testid="nav-bundle-ribbon-dismiss"
            className="absolute right-3 top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100 p-1"
          >
            <X size={14} />
          </button>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-36">
        <button
          data-testid={HOME.navBrand}
          onClick={() => (onHome ? scrollTo('hero') : navigate('/'))}
          className={`flex items-center gap-5 text-left transition-colors group whitespace-nowrap ${
            solid ? 'text-[var(--brand-forest)]' : 'text-[var(--brand-parchment)]'
          }`}
        >
          <span
            className="shrink-0 transition-transform duration-500 group-hover:rotate-[8deg]"
            aria-hidden
          >
            <BrandLogo size={160} />
          </span>
          <span
            className="leading-tight whitespace-nowrap"
            style={{
              // Soft text-shadow keeps the wordmark legible over the
              // Highland hero without any pill / border / box treatment.
              textShadow: solid
                ? 'none'
                : '0 1px 2px rgba(0,0,0,0.55), 0 2px 12px rgba(0,0,0,0.35)',
            }}
          >
            <span className="block font-display text-3xl sm:text-4xl tracking-tight">EcoBike Camping</span>
            <span className="block text-sm uppercase tracking-[0.28em] opacity-80">
              Supplies Co.
            </span>
          </span>
        </button>

        <nav className="hidden xl:flex items-center gap-8 whitespace-nowrap">
          {SCROLL_LINKS.filter((l) => l.primary).map((l) => (
            <button
              key={l.id}
              data-testid={l.testId}
              onClick={() => scrollTo(l.id)}
              className={`link-expand text-sm font-medium tracking-wide transition-colors ${
                solid
                  ? 'text-[var(--brand-forest)] hover:text-[var(--brand-olive)]'
                  : 'text-[var(--brand-parchment)] hover:text-[var(--brand-gold)]'
              }`}
            >
              {l.label}
            </button>
          ))}
          {ROUTE_LINKS.filter((l) => l.primary).map((l) => (
            <button
              key={l.path}
              data-testid={l.testId}
              onClick={() => goToRoute(l.path)}
              className={`link-expand text-sm font-medium tracking-wide transition-colors ${
                solid
                  ? 'text-[var(--brand-forest)] hover:text-[var(--brand-olive)]'
                  : 'text-[var(--brand-parchment)] hover:text-[var(--brand-gold)]'
              }`}
            >
              {l.label}
            </button>
          ))}
          <button
            key={CONTACT_LINK.path}
            data-testid={CONTACT_LINK.testId}
            onClick={() => goToRoute(CONTACT_LINK.path)}
            className={`link-expand text-sm font-medium tracking-wide transition-colors ${
              solid
                ? 'text-[var(--brand-forest)] hover:text-[var(--brand-olive)]'
                : 'text-[var(--brand-parchment)] hover:text-[var(--brand-gold)]'
            }`}
          >
            {CONTACT_LINK.label}
          </button>

          {/* Wishlist heart badge — visible on all pages */}
          <button
            type="button"
            data-testid={WISHLIST.navCount}
            onClick={() => setWishlistOpen(true)}
            aria-label={`Open saved items (${wishlistCount})`}
            className={`relative inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${
              solid
                ? 'text-[var(--brand-forest)] hover:text-[var(--brand-olive)]'
                : 'text-[var(--brand-parchment)] hover:text-[var(--brand-gold)]'
            }`}
          >
            <Heart size={18} className={wishlistCount > 0 ? 'fill-[var(--brand-gold)] text-[var(--brand-gold)]' : ''} />
            {wishlistCount > 0 && (
              <span
                data-testid="wishlist-nav-badge"
                className="text-[11px] font-bold tabular-nums bg-[var(--brand-gold)] text-[var(--brand-forest-deep)] px-1.5 py-0.5 leading-none"
              >
                {wishlistCount}
              </span>
            )}
          </button>

          <button
            data-testid={HOME.navEnquireCta}
            onClick={() => goToRoute('/contact')}
            className="btn-gold px-5 py-2.5 text-sm font-semibold rounded-none"
          >
            Make an Enquiry
          </button>

          {/* Customer account link — Sign in or avatar */}
          {!userLoading && (
            user ? (
              <button
                type="button"
                data-testid="nav-account-avatar"
                onClick={() => navigate('/account')}
                aria-label={`Signed in as ${user.name}. Open account`}
                className="ml-1 grid place-items-center w-9 h-9 overflow-hidden border-2 border-[var(--brand-gold)] hover:border-[var(--brand-olive)] transition-colors"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <UserCircle2
                    size={22}
                    className={solid ? 'text-[var(--brand-forest)]' : 'text-[var(--brand-parchment)]'}
                  />
                )}
              </button>
            ) : (
              <button
                type="button"
                data-testid="nav-signin"
                onClick={() => navigate('/account')}
                className={`ml-1 inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${
                  solid
                    ? 'text-[var(--brand-forest)] hover:text-[var(--brand-olive)]'
                    : 'text-[var(--brand-parchment)] hover:text-[var(--brand-gold)]'
                }`}
              >
                <LogIn size={15} /> Sign in
              </button>
            )
          )}
        </nav>

        {/* Mobile: compact wishlist heart before the burger */}
        <div className="xl:hidden flex items-center gap-3">
          <button
            type="button"
            data-testid={`${WISHLIST.navCount}-mobile`}
            onClick={() => setWishlistOpen(true)}
            aria-label={`Open saved items (${wishlistCount})`}
            className={`relative inline-flex items-center gap-1 ${
              solid ? 'text-[var(--brand-forest)]' : 'text-[var(--brand-parchment)]'
            }`}
          >
            <Heart size={20} className={wishlistCount > 0 ? 'fill-[var(--brand-gold)] text-[var(--brand-gold)]' : ''} />
            {wishlistCount > 0 && (
              <span className="text-[10px] font-bold tabular-nums bg-[var(--brand-gold)] text-[var(--brand-forest-deep)] px-1 py-0.5 leading-none">
                {wishlistCount}
              </span>
            )}
          </button>

          <button
            data-testid={HOME.navMobileToggle}
            aria-label="Toggle navigation"
            onClick={() => setOpen((v) => !v)}
            className={`grid place-items-center w-10 h-10 border ${
              solid
                ? 'border-[var(--brand-forest)] text-[var(--brand-forest)]'
                : 'border-[var(--brand-parchment)] text-[var(--brand-parchment)]'
            }`}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-[var(--brand-forest-deep)] text-[var(--brand-parchment)] border-t border-white/10">
          <div className="px-6 py-6 flex flex-col gap-4">
            {SCROLL_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`${l.testId}-mobile`}
                onClick={() => scrollTo(l.id)}
                className="text-left font-display text-2xl tracking-tight"
              >
                {l.label}
              </button>
            ))}
            {ROUTE_LINKS.map((l) => (
              <button
                key={l.path}
                data-testid={`${l.testId}-mobile`}
                onClick={() => goToRoute(l.path)}
                className="text-left font-display text-2xl tracking-tight"
              >
                {l.label}
              </button>
            ))}
            <button
              key={CONTACT_LINK.path}
              data-testid={`${CONTACT_LINK.testId}-mobile`}
              onClick={() => goToRoute(CONTACT_LINK.path)}
              className="text-left font-display text-2xl tracking-tight"
            >
              {CONTACT_LINK.label}
            </button>
            <button
              onClick={() => goToRoute('/contact')}
              className="btn-gold mt-2 py-3 font-semibold flex items-center justify-center gap-2"
            >
              <Compass size={16} /> Make an Enquiry
            </button>
            {!userLoading && (
              <button
                type="button"
                data-testid="nav-signin-mobile"
                onClick={() => { setOpen(false); navigate('/account'); }}
                className="text-left font-display text-2xl tracking-tight inline-flex items-center gap-2"
              >
                {user ? (
                  <>
                    <UserCircle2 size={22} /> {user.name?.split(' ')[0] || 'My account'}
                  </>
                ) : (
                  <>
                    <LogIn size={20} /> Sign in
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
      <WishlistPanel open={wishlistOpen} onClose={() => setWishlistOpen(false)} />
    </header>
  );
}
